import React, { useEffect, useMemo, useState } from "react";
import CardComponent from "../../../../common/components/card-component/card-component";
import TextInputComponent from "../../../../common/components/text-input-component/text-input-component";
import personalInfoIcon from "../../../../assets/icons/personal-info.svg";
import companyInfoIcon from "../../../../assets/icons/company-info.svg";
import companyAddressIcon from "../../../../assets/icons/company-address.svg";
import billingAddressIcon from "../../../../assets/icons/billing-address.svg";
import "./basic-details.scss";
import { useFormikContext, FormikTouched } from "formik";
import CustomCheckbox from "../../../../common/components/custom-checkbox-component/custom-checkbox-component";
import UploadImage from "../../../../common/components/upload-image/upload-image";
import { CompanyFormValues } from "../add-new-tenet-helper";
import { getTimezones } from "../../../../constants/timezones";
import ColorthemeInputComponent from "../../../../common/components/colortheme-input-component/colortheme-input-component";
import SearchableAutoSelect from "../../../../common/components/auto-select-search-component/search-select-component";
import { InputLabel } from "@mui/material";
import CountryCodeWithPhoneNumber from "../../../../common/components/phone-number-input-component/country-code-phone-numner-input";
import { SelectInputComponent } from "../../../../common/components";
import AddressFormFields from "../../../../common/components/address-form-fields/address-form-fields";
import { debounce } from "lodash";
import { validatePartnerCompany } from "../../../../api/tenets";
import { useSetLoading } from "../../../../common/hooks/appearance";

interface BasicDetailsFormValues {
  values: CompanyFormValues;
  errors: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  touched: FormikTouched<CompanyFormValues>;
  checked: boolean;
  setChecked: (checked: boolean) => void;
  setIsExistCompanyName?: any;
  isExistCompanyName?: boolean;
  submitAttempted?: boolean;
}

const PRODUCT_OPTIONS = [
  { value: "At-Need", label: "At-Need" },
  { value: "Preneed", label: "Preneed" },
];

const BasicDetailsStep: React.FC<BasicDetailsFormValues> = ({
  values,
  errors,
  handleChange,
  touched,
  checked,
  setChecked,
  setIsExistCompanyName,
  isExistCompanyName = false,
  submitAttempted = false,
}) => {
  const { setFieldValue, handleBlur, setFieldTouched } =
    useFormikContext<CompanyFormValues>();
  const timezones = getTimezones();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleBillingCheckBox = (checked: boolean) => {
    setChecked(checked);
    if (checked) {
      setFieldValue("billingAddress", values.address);
    } else {
      setFieldValue("billingAddress", {
        streetLine1: "",
        streetLine2: "",
        city: "",
        state: "",
        country: "",
        postcode: "",
      });
    }
  };

  const validateCompanyName = async (companyName: string) => {
    try {
      setIsLoading(true);
      const resp: any = await validatePartnerCompany({
        companyName: companyName.trim(),
      });
      if (resp?.data?.data?.exists) {
        setIsExistCompanyName(true);
      } else {
        setIsExistCompanyName(false);
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      console.error("Validation error:", e);
    }
  };

  const debouncedValidate = useMemo(
    () => debounce(validateCompanyName, 500),
    []
  );

  useEffect(() => {
    if (values?.companyName?.trim().length > 1)
      debouncedValidate(values.companyName);
    else {
      setIsExistCompanyName(false);
    }
  }, [values.companyName]);

  // Sync billing address with company address when checkbox is checked
  useEffect(() => {
    if (checked && values.address) {
      setFieldValue("billingAddress", values.address);
    }
  }, [checked, values.address, setFieldValue]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div className="basic-details">
      <CardComponent title="Personal Information" icon={personalInfoIcon}>
        <div className="form-row">
          <div className="form-group">
            <TextInputComponent
              fieldName="users[0].firstName"
              label="First Name"
              placeholder="Enter first name"
              value={values?.users?.[0]?.firstName || ""}
              onChange={handleChange}
              minlength="3"
              maxlength="50"
              type="text"
              optional
              isAlphaNumeric
              handleBlur={handleBlur}
            />
          </div>
          <div className="form-group">
            <TextInputComponent
              fieldName="users[0].lastName"
              label="Last Name"
              placeholder="Enter last name"
              value={values?.users?.[0]?.lastName || ""}
              onChange={handleChange}
              minlength="3"
              maxlength="50"
              type="text"
              optional
              isAlphaNumeric
              handleBlur={handleBlur}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <TextInputComponent
              fieldName="users[0].email"
              label="Email Address"
              placeholder="Enter email"
              value={values?.users?.[0]?.email || ""}
              onChange={handleChange}
              maxlength="150"
              type="email"
              optional
              handleBlur={handleBlur}
            />
          </div>
          <div className="form-group">
            <CountryCodeWithPhoneNumber
              fieldName="phonenumber"
              label="Phone Number"
              placeholder="Enter phone number"
              onChange={(value: string, country: any) => {
                const countryCode = country?.dialCode;

                if (!countryCode) {
                  console.warn("Missing dialCode in country object");
                  return;
                }

                const numberOnly = value.slice(countryCode.length + 1);
                const formatted = `+${countryCode} ${numberOnly}`;

                setFieldValue("users[0].phoneNumber", formatted);
              }}
              onBlur={() => {
                setFieldTouched("users[0].phoneNumber", true);
              }}
              value={values?.users?.[0]?.phoneNumber || ""}
              defaultCountry="us"
              // countryCodeEditable
            />
            {errors?.users?.[0]?.phoneNumber &&
              submitAttempted && (
                <div className="error-message">
                  {errors.users[0].phoneNumber}
                </div>
              )}
          </div>
        </div>
      </CardComponent>

      <CardComponent title="Company Information" icon={companyInfoIcon}>
        <div className="form-row">
          <div className="form-group">
            <div className="logo-container">
              <div className="upload-logo">
                <UploadImage
                  label="Upload Logo *"
                  text="logo"
                  fieldName="logo_url"
                  value={values?.logo_url}
                  onChange={(file: File) => {
                    setFieldValue("logo_url", file);
                  }}
                  showCloseButton
                  handleClose={() => {
                    setFieldValue("logo_url", null);
                  }}
                />
                {errors?.logo_url && touched?.logo_url && (
                  <div className="error-message">
                    {typeof errors?.logo_url === "string"
                      ? errors.logo_url
                      : ""}
                  </div>
                )}
              </div>
              <div className="container">
                <label className="color-label">Custom Color Theme</label>
                <div className="color-container">
                  <ColorthemeInputComponent
                    value={values?.primaryColor || ""}
                    label="Primary Color"
                    name="primaryColor"
                    handleColorChange={(
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const newColor = e.target.value;
                      setFieldValue("primaryColor", newColor);
                    }}
                  />
                  <ColorthemeInputComponent
                    value={values?.accentColor || ""}
                    label="Accent Color"
                    name="accentColor"
                    handleColorChange={(
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const newColor = e.target.value;
                      setFieldValue("accentColor", newColor);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <TextInputComponent
              fieldName="companyName"
              label="Company Name"
              placeholder="Enter company name"
              value={values?.companyName || ""}
              onChange={handleChange}
              maxlength="150"
              type="text"
              optional
              allowSpecialCharacters
              handleBlur={handleBlur}
            />
            {isExistCompanyName && (
              <div className="error-message">Company name already exists</div>
            )}
          </div>
          <div className="form-group">
            <TextInputComponent
              fieldName="companyWebsite"
              label="Company Website"
              placeholder="Enter company website"
              value={values?.companyWebsite || ""}
              onChange={handleChange}
              type="text"
              maxlength="150"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group time-zone-wrapper">
            <InputLabel required={true} htmlFor={"timezonE"}>
              {"Time Zone"}
            </InputLabel>
            <SearchableAutoSelect
              fieldName="timezone"
              label="Time Zone"
              optional
              options={timezones}
              isShowError={!values?.timezone}
              placeholder="Select time zone"
              //auto complete expects value as object
              value={
                timezones.find((opt) => opt.value === values?.timezone) || null
              }
              onChange={(e: any) => {
                setFieldValue("timezone", e);
              }}
              handleBlur={() => {
                setFieldTouched("timezone", true);
              }}
            />
          </div>
          <div className="form-group">
            <TextInputComponent
              fieldName="employeeCount"
              label="Employee Count"
              placeholder="Enter employee count"
              value={values?.employeeCount || ""}
              onChange={handleChange}
              type="text"
              maxlength="6"
              isNumber
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <TextInputComponent
              fieldName="email"
              label="Email Address"
              placeholder="Enter email"
              value={values?.email || ""}
              onChange={handleChange}
              maxlength="150"
              type="email"
              optional
              handleBlur={handleBlur}
            />
          </div>
          <div className="form-group">
            <CountryCodeWithPhoneNumber
              fieldName="phonenumber"
              label="Phone Number"
              placeholder="Enter phone number"
              onChange={(value: string, country: any) => {
                const countryCode = country?.dialCode;

                if (!countryCode) {
                  console.warn("Missing dialCode in country object");
                  return;
                }

                const numberOnly = value.slice(countryCode.length + 1);
                const formatted = `+${countryCode} ${numberOnly}`;

                setFieldValue("phoneNumber", formatted);
              }}
              onBlur={() => {
                setFieldTouched("phoneNumber", true);
              }}
              value={values?.phoneNumber || ""}
              defaultCountry="us"
              // countryCodeEditable
            />
            {errors?.phoneNumber && submitAttempted && (
              <div className="error-message">{errors.phoneNumber}</div>
            )}
          </div>
        </div>
        <div className="form-row product-row">
          <div className="form-group">
            <SelectInputComponent
              fieldName="products"
              options={PRODUCT_OPTIONS}
              value={values.products || []}
              onChange={(e: any) => setFieldValue("products", e)}
              placeholder="Select product"
              label="Products"
              multiple={true}
              tabs={true}
            />
          </div>
        </div>
      </CardComponent>

      <div className="client-address">
        <CardComponent title="Company Address" icon={companyAddressIcon}>
          <AddressFormFields namePrefix="address" />
        </CardComponent>
        <CardComponent
          title="Billing Address"
          icon={billingAddressIcon}
          headerRight={
            <CustomCheckbox
              checked={checked}
              onChange={handleBillingCheckBox}
              label="Same as company address"
            />
          }
        >
          <AddressFormFields namePrefix="billingAddress" disabled={checked} />
        </CardComponent>
      </div>
    </div>
  );
};

export default BasicDetailsStep;
